import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import HeaderView from "../../../components/admin/headerView";
import ListingTableHeader from "../../../components/listingTableHeader";
import AdminTable from "../../../components/adminTable";
import { TAdminTableRowData } from "../../../components/types";
import { useGetStandardAssetIconsListQuery } from "../../../api/network/adminApiServices";
import {
  STANDARD_ICONS_LIST,
  STANDARD_ICONS_SLUGS,
} from "../../admin-details/account/constants";

const StandardIconsListing: React.FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.import_export",
  });

  const navigate = useNavigate();
  const listingWrapperRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");
  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const { data: apiData, isLoading } = useGetStandardAssetIconsListQuery();
  const getResultsArray = () => {
    if (!apiData) return [];
    return Array.isArray(apiData)
      ? apiData
      : apiData.results || apiData.data || [];
  };

  useEffect(() => {
    if (apiData) {
      setFilteredResults(getResultsArray());
    }
  }, [apiData]);

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.importExport);
  };

  const debouncedSearch = useDebouncedCallback((value: string) => {
    const allResults = getResultsArray();
    if (!value.trim()) {
      setFilteredResults(allResults);
    } else {
      const filtered = allResults.filter((item: any) =>
        item.identifier_text?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredResults(filtered);
    }
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleSearchClear = () => {
    setSearch("");
    setFilteredResults(getResultsArray());
  };

  const handleCopy = (text: string, itemId: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(itemId);
    setTimeout(() => {
      setCopiedId(null);
    }, 1000);
  };

  const columns = [
    { name: "Icons", show: true },
    { name: "Identifier Text", show: true },
    { name: "Actions", show: true },
  ];

  const getIconImage = (identifierText: string) => {
    const slugValue = (STANDARD_ICONS_SLUGS as Record<string, string>)[
      identifierText
    ];
    const matchedIcon = STANDARD_ICONS_LIST.find(
      (iconObj) => iconObj.key === slugValue
    );
    return matchedIcon ? matchedIcon.icon : null;
  };

  const tableData: TAdminTableRowData[] = filteredResults.map((item) => {
    const matchedIconImg = getIconImage(item.identifier_text);

    return {
      navLink: "",
      cellData: [
        <div key={`img-${item.id}`} className="flex items-center justify-start">
          {matchedIconImg ? (
            <img src={matchedIconImg} alt="icon" className="size-8" />
          ) : (
            <span className="text-gray-400 text-xs">-</span>
          )}
        </div>,
        <span
          key={`text-${item.id}`}
          className="font-mono text-sm text-gray-700"
        >
          {item.identifier_text}
        </span>,
        <div key={`action-${item.id}`} className="flex items-center">
          <button
            onClick={(e) => {
              e.preventDefault();
              handleCopy(item.identifier_text, item.id);
            }}
            className="text-primary-like hover:text-blue-700 font-semibold"
            type="button"
          >
            {copiedId === item.id ? (
              <span className="text-green-600">Copied!</span>
            ) : (
              <span>Copy</span>
            )}
          </button>
        </div>,
      ],
      isActive: true,
    };
  });

  return (
    <div className="bg-white min-h-screen">
      <HeaderView
        title="Asset Standard Icons"
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />

      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingTableHeader
          heading="Standard Icon Listing"
          searchBoxPlaceholder={t("search_placeholder") || "Search URLs..."}
          searchBoxValue={search}
          searchBoxOnChange={handleSearchChange}
          searchBoxOnClear={handleSearchClear}
          filters={[]}
          activeFilterSlug=""
          handleFilterChange={() => {}}
        />

        <div className="py-4 mt-4 relative">
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns.filter((col) => col.show).map((col) => col.name)}
            data={tableData}
            isLoading={isLoading}
            listingWrapperRef={listingWrapperRef}
          />
        </div>
      </div>
    </div>
  );
};

export default StandardIconsListing;
